import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Switch,
  FormControlLabel,
  Divider,
  Button,
  TextField,
  Alert,
  Grid,
  Card,
  CardContent,
  CardActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Avatar,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Notifications as NotificationsIcon,
  Api as IntegrationIcon,
  Security as SecurityIcon,
  DisplaySettings as DisplayIcon,
  Tag as TagIcon,
  Folder as FolderIcon,
  Save as SaveIcon,
  CloudSync as CloudSyncIcon,
  Email as EmailIcon,
  CalendarToday as CalendarIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Analytics as AnalyticsIcon,
  Backup as BackupIcon,
  Download as DownloadIcon,
  Upload as UploadIcon,
  Sync as SyncIcon,
  Settings as SettingsIcon,
  Contacts as ContactsIcon,
  Campaign as CampaignIcon,
  Assessment as ReportsIcon,
  AutoAwesome as AutomationIcon,
  Webhook as WebhookIcon,
  Key as ApiKeyIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Group as TeamIcon,
  Money as PricingIcon,
  Help as HelpIcon,
  Feedback as FeedbackIcon,
  Info as InfoIcon,
  ColorLens as ColorLensIcon,
} from '@mui/icons-material';
import { makeApiCall } from '../global/apiCall';
import GroupsManager from '../components/modern/GroupsManager';
import TagsManager from '../components/modern/TagsManager';
import PricingCard from '../components/PricingCard';
import { useTheme as useCustomTheme } from '../contexts/ThemeContext';

function Setting() {
  const { themeSettings, updateThemeSettings, toggleDarkMode, fontFamilies, isDark } = useCustomTheme();
  const [settings, setSettings] = useState({
    theme: 'light',
    notifications: {
      email: true,
      push: true,
      followUpReminders: true,
      marketing: false,
      weeklyReports: true,
    },
    integrations: {
      crm: {
        enabled: false,
        platform: null,
        autoSync: false,
        syncFrequency: 'daily',
        fieldMapping: {},
        webhookUrl: '',
      },
      calendar: {
        enabled: false,
        provider: null,
        autoCreateEvents: false,
      },
      email: {
        enabled: false,
        provider: null,
        templates: true,
      },
      marketing: {
        enabled: false,
        platform: null,
        autoSegmentation: false,
      },
      analytics: {
        enabled: false,
        platform: null,
        trackingId: '',
      },
    },
    privacy: {
      shareAnalytics: true,
      backupData: true,
      dataRetention: '2years',
      cookieConsent: true,
    },
    display: {
      cardsPerPage: 10,
      sortBy: 'updatedAt',
      sortOrder: 'desc',
      defaultView: 'grid',
      compactMode: false,
    },
    automation: {
      followUpReminders: true,
      autoTagging: false,
      duplicateDetection: true,
      scoreLeads: false,
    },
    team: {
      sharing: false,
      permissions: 'view',
      defaultGroup: null,
    },
    api: {
      enabled: false,
      apiKey: '',
      webhooks: [],
    },
  });
  
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [groupsDialog, setGroupsDialog] = useState(false);
  const [tagsDialog, setTagsDialog] = useState(false);

  useEffect(() => {
    fetchUserSettings();
  }, []);

  const fetchUserSettings = async () => {
    try {
      const response = await makeApiCall('/profile', 'GET');
      if (response.success && response.data.settings) {
        setSettings(prevSettings => ({
          ...prevSettings,
          ...response.data.settings
        }));
      }
    } catch (error) {
      console.error('Error fetching user settings:', error);
    }
  };

  const updateSettings = async (newSettings) => {
    setLoading(true);
    try {
      const response = await makeApiCall('/updateUserSetting', 'POST', {
        settings: newSettings
      });
      
      if (response.success) {
        setSettings(prevSettings => ({
          ...prevSettings,
          ...newSettings
        }));
        setMessage({ type: 'success', text: 'Settings updated successfully!' });
      } else {
        setMessage({ type: 'error', text: 'Failed to update settings' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Error updating settings: ' + error.message });
    } finally {
      setLoading(false);
    }
  };

  const handleSettingChange = (path, value) => {
    const newSettings = { ...settings };
    const keys = path.split('.');
    let current = newSettings;
    
    for (let i = 0; i < keys.length - 1; i++) {
      current = current[keys[i]];
    }
    current[keys[keys.length - 1]] = value;
    
    updateSettings(newSettings);
  };

  return (
    <Box sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 600 }}>
        Settings
      </Typography>

      {message.text && (
        <Alert 
          severity={message.type} 
          sx={{ mb: 3 }} 
          onClose={() => setMessage({ type: '', text: '' })}
        >
          {message.text}
        </Alert>
      )}

      {/* Current Plan & Pricing */}
      <Accordion defaultExpanded sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <PricingIcon sx={{ mr: 2, color: 'primary.main' }} />
            <Typography variant="h6">Current Plan & Pricing</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <PricingCard />
        </AccordionDetails>
      </Accordion>

      {/* Organization & Management */}
      <Accordion defaultExpanded sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <FolderIcon sx={{ mr: 2, color: 'primary.main' }} />
            <Typography variant="h6">Organization & Management</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    <FolderIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Groups
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Organize your business cards into groups for better management
                  </Typography>
                </CardContent>
                <CardActions>
                  <Button 
                    variant="outlined" 
                    onClick={() => setGroupsDialog(true)}
                    startIcon={<FolderIcon />}
                  >
                    Manage Groups
                  </Button>
                </CardActions>
              </Card>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    <TagIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Tags
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Create and manage tags to categorize your contacts
                  </Typography>
                </CardContent>
                <CardActions>
                  <Button 
                    variant="outlined" 
                    onClick={() => setTagsDialog(true)}
                    startIcon={<TagIcon />}
                  >
                    Manage Tags
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Theme & Appearance */}
      <Accordion sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <DisplayIcon sx={{ mr: 2, color: 'primary.main' }} />
            <Typography variant="h6">Theme & Appearance</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            {/* Dark Mode Toggle */}
            <Grid item xs={12} sm={6} md={4}>
              <Paper sx={{ p: 3, textAlign: 'center', height: '100%' }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <DisplayIcon sx={{ mr: 1 }} />
                  Dark Mode
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Switch between light and dark themes
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={isDark}
                      onChange={toggleDarkMode}
                      size="large"
                    />
                  }
                  label={isDark ? "Dark Mode" : "Light Mode"}
                />
              </Paper>
            </Grid>

            {/* Font Family Selection */}
            <Grid item xs={12} sm={6} md={4}>
              <Paper sx={{ p: 3, height: '100%' }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                  <SettingsIcon sx={{ mr: 1 }} />
                  Typography
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Choose your preferred font family
                </Typography>
                <FormControl fullWidth>
                  <InputLabel>Font Family</InputLabel>
                  <Select
                    value={themeSettings.fontFamily}
                    onChange={(e) => updateThemeSettings({ fontFamily: e.target.value })}
                    size="small"
                  >
                    <MenuItem value="inter" sx={{ fontFamily: '"Inter", sans-serif' }}>
                      Inter (Modern)
                    </MenuItem>
                    <MenuItem value="poppins" sx={{ fontFamily: '"Poppins", sans-serif' }}>
                      Poppins (Friendly)
                    </MenuItem>
                    <MenuItem value="montserrat" sx={{ fontFamily: '"Montserrat", sans-serif' }}>
                      Montserrat (Professional)
                    </MenuItem>
                    <MenuItem value="playfair" sx={{ fontFamily: '"Playfair Display", serif' }}>
                      Playfair (Elegant)
                    </MenuItem>
                    <MenuItem value="roboto" sx={{ fontFamily: '"Roboto", sans-serif' }}>
                      Roboto (Classic)
                    </MenuItem>
                  </Select>
                </FormControl>
              </Paper>
            </Grid>

            {/* Layout Style */}
            <Grid item xs={12} sm={6} md={4}>
              <Paper sx={{ p: 3, height: '100%' }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                  <DisplayIcon sx={{ mr: 1 }} />
                  Layout Style
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Choose your preferred layout style
                </Typography>
                <FormControl fullWidth>
                  <InputLabel>Layout</InputLabel>
                  <Select
                    value={themeSettings.layoutStyle || 'modern'}
                    onChange={(e) => updateThemeSettings({ layoutStyle: e.target.value })}
                    size="small"
                  >
                    <MenuItem value="modern">Modern Cards</MenuItem>
                    <MenuItem value="minimal">Minimal</MenuItem>
                    <MenuItem value="compact">Compact</MenuItem>
                    <MenuItem value="luxurious">Luxurious</MenuItem>
                  </Select>
                </FormControl>
              </Paper>
            </Grid>

            {/* Color Scheme Preview */}
            <Grid item xs={12}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <ColorLensIcon sx={{ mr: 1 }} />
                  Color Scheme Preview
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box 
                      sx={{ 
                        width: 24, 
                        height: 24, 
                        bgcolor: 'primary.main', 
                        borderRadius: '50%',
                        boxShadow: 1 
                      }} 
                    />
                    <Typography variant="body2">Primary</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box 
                      sx={{ 
                        width: 24, 
                        height: 24, 
                        bgcolor: 'secondary.main', 
                        borderRadius: '50%',
                        boxShadow: 1 
                      }} 
                    />
                    <Typography variant="body2">Secondary</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box 
                      sx={{ 
                        width: 24, 
                        height: 24, 
                        bgcolor: 'success.main', 
                        borderRadius: '50%',
                        boxShadow: 1 
                      }} 
                    />
                    <Typography variant="body2">Success</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box 
                      sx={{ 
                        width: 24, 
                        height: 24, 
                        bgcolor: 'warning.main', 
                        borderRadius: '50%',
                        boxShadow: 1 
                      }} 
                    />
                    <Typography variant="body2">Warning</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box 
                      sx={{ 
                        width: 24, 
                        height: 24, 
                        bgcolor: 'error.main', 
                        borderRadius: '50%',
                        boxShadow: 1 
                      }} 
                    />
                    <Typography variant="body2">Error</Typography>
                  </Box>
                </Box>
                
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  Current theme: <strong>{isDark ? 'Dark' : 'Light'}</strong> • 
                  Font: <strong>{themeSettings.fontFamily}</strong> • 
                  Layout: <strong>{themeSettings.layoutStyle || 'modern'}</strong>
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Integrations */}
      <Accordion sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IntegrationIcon sx={{ mr: 2, color: 'primary.main' }} />
            <Typography variant="h6">Integrations</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            {/* CRM Integration */}
            <Grid item xs={12} md={6}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <CloudSyncIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="h6">CRM Integration</Typography>
                    {settings.integrations?.crm?.enabled && (
                      <CheckCircleIcon sx={{ ml: 'auto', color: 'success.main' }} />
                    )}
                  </Box>
                  
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.integrations?.crm?.enabled || false}
                        onChange={(e) => handleSettingChange('integrations.crm.enabled', e.target.checked)}
                      />
                    }
                    label="Enable CRM Sync"
                  />
                  
                  {settings.integrations?.crm?.enabled && (
                    <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <FormControl fullWidth size="small">
                        <InputLabel>CRM Platform</InputLabel>
                        <Select
                          value={settings.integrations?.crm?.platform || ''}
                          onChange={(e) => handleSettingChange('integrations.crm.platform', e.target.value)}
                        >
                          <MenuItem value="salesforce">
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <BusinessIcon sx={{ mr: 1 }} /> Salesforce
                            </Box>
                          </MenuItem>
                          <MenuItem value="hubspot">
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <BusinessIcon sx={{ mr: 1 }} /> HubSpot
                            </Box>
                          </MenuItem>
                          <MenuItem value="zoho">
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <BusinessIcon sx={{ mr: 1 }} /> Zoho CRM
                            </Box>
                          </MenuItem>
                          <MenuItem value="pipedrive">
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <BusinessIcon sx={{ mr: 1 }} /> Pipedrive
                            </Box>
                          </MenuItem>
                        </Select>
                      </FormControl>
                      
                      <FormControl fullWidth size="small">
                        <InputLabel>Sync Frequency</InputLabel>
                        <Select
                          value={settings.integrations?.crm?.syncFrequency || 'daily'}
                          onChange={(e) => handleSettingChange('integrations.crm.syncFrequency', e.target.value)}
                        >
                          <MenuItem value="realtime">Real-time</MenuItem>
                          <MenuItem value="hourly">Hourly</MenuItem>
                          <MenuItem value="daily">Daily</MenuItem>
                          <MenuItem value="weekly">Weekly</MenuItem>
                        </Select>
                      </FormControl>
                      
                      <TextField
                        fullWidth
                        size="small"
                        label="Webhook URL (Optional)"
                        value={settings.integrations?.crm?.webhookUrl || ''}
                        onChange={(e) => handleSettingChange('integrations.crm.webhookUrl', e.target.value)}
                        placeholder="https://your-crm.com/webhook"
                      />
                      
                      <FormControlLabel
                        control={
                          <Switch
                            checked={settings.integrations?.crm?.autoSync || false}
                            onChange={(e) => handleSettingChange('integrations.crm.autoSync', e.target.checked)}
                          />
                        }
                        label="Auto Sync New Contacts"
                      />
                      
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<SyncIcon />}
                        disabled={!settings.integrations?.crm?.platform}
                      >
                        Sync Now
                      </Button>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Marketing Integration */}
            <Grid item xs={12} md={6}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <CampaignIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="h6">Marketing Automation</Typography>
                    {settings.integrations?.marketing?.enabled && (
                      <CheckCircleIcon sx={{ ml: 'auto', color: 'success.main' }} />
                    )}
                  </Box>
                  
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.integrations?.marketing?.enabled || false}
                        onChange={(e) => handleSettingChange('integrations.marketing.enabled', e.target.checked)}
                      />
                    }
                    label="Enable Marketing Integration"
                  />
                  
                  {settings.integrations?.marketing?.enabled && (
                    <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Platform</InputLabel>
                        <Select
                          value={settings.integrations?.marketing?.platform || ''}
                          onChange={(e) => handleSettingChange('integrations.marketing.platform', e.target.value)}
                        >
                          <MenuItem value="mailchimp">Mailchimp</MenuItem>
                          <MenuItem value="klaviyo">Klaviyo</MenuItem>
                          <MenuItem value="activecampaign">ActiveCampaign</MenuItem>
                          <MenuItem value="constantcontact">Constant Contact</MenuItem>
                        </Select>
                      </FormControl>
                      
                      <FormControlLabel
                        control={
                          <Switch
                            checked={settings.integrations?.marketing?.autoSegmentation || false}
                            onChange={(e) => handleSettingChange('integrations.marketing.autoSegmentation', e.target.checked)}
                          />
                        }
                        label="Auto-segment Contacts"
                      />
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Analytics Integration */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <ReportsIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="h6">Analytics</Typography>
                    {settings.integrations?.analytics?.enabled && (
                      <CheckCircleIcon sx={{ ml: 'auto', color: 'success.main' }} />
                    )}
                  </Box>
                  
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.integrations?.analytics?.enabled || false}
                        onChange={(e) => handleSettingChange('integrations.analytics.enabled', e.target.checked)}
                      />
                    }
                    label="Enable Analytics Tracking"
                  />
                  
                  {settings.integrations?.analytics?.enabled && (
                    <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Platform</InputLabel>
                        <Select
                          value={settings.integrations?.analytics?.platform || ''}
                          onChange={(e) => handleSettingChange('integrations.analytics.platform', e.target.value)}
                        >
                          <MenuItem value="google">Google Analytics</MenuItem>
                          <MenuItem value="mixpanel">Mixpanel</MenuItem>
                          <MenuItem value="segment">Segment</MenuItem>
                          <MenuItem value="amplitude">Amplitude</MenuItem>
                        </Select>
                      </FormControl>
                      
                      <TextField
                        fullWidth
                        size="small"
                        label="Tracking ID"
                        value={settings.integrations?.analytics?.trackingId || ''}
                        onChange={(e) => handleSettingChange('integrations.analytics.trackingId', e.target.value)}
                        placeholder="G-XXXXXXXXXX or UA-XXXXXXX-X"
                      />
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Calendar Integration */}
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    <CalendarIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Calendar Integration
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.integrations?.calendar?.enabled || false}
                        onChange={(e) => handleSettingChange('integrations.calendar.enabled', e.target.checked)}
                      />
                    }
                    label="Enable Calendar Sync"
                  />
                  {settings.integrations?.calendar?.enabled && (
                    <Box sx={{ mt: 2 }}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Calendar Provider</InputLabel>
                        <Select
                          value={settings.integrations?.calendar?.provider || ''}
                          onChange={(e) => handleSettingChange('integrations.calendar.provider', e.target.value)}
                        >
                          <MenuItem value="google">Google Calendar</MenuItem>
                          <MenuItem value="outlook">Outlook</MenuItem>
                          <MenuItem value="apple">Apple Calendar</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Email Integration */}
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    <EmailIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Email Integration
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.integrations?.email?.enabled || false}
                        onChange={(e) => handleSettingChange('integrations.email.enabled', e.target.checked)}
                      />
                    }
                    label="Enable Email Sync"
                  />
                  {settings.integrations?.email?.enabled && (
                    <Box sx={{ mt: 2 }}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Email Provider</InputLabel>
                        <Select
                          value={settings.integrations?.email?.provider || ''}
                          onChange={(e) => handleSettingChange('integrations.email.provider', e.target.value)}
                        >
                          <MenuItem value="gmail">Gmail</MenuItem>
                          <MenuItem value="outlook">Outlook</MenuItem>
                          <MenuItem value="yahoo">Yahoo Mail</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Automation & Smart Features */}
      <Accordion sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <AutomationIcon sx={{ mr: 2, color: 'primary.main' }} />
            <Typography variant="h6">Automation & Smart Features</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Paper sx={{ p: 2, height: '100%' }}>
                <Typography variant="subtitle1" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                  <ScheduleIcon sx={{ mr: 1 }} /> Follow-up Automation
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.automation?.followUpReminders || false}
                      onChange={(e) => handleSettingChange('automation.followUpReminders', e.target.checked)}
                    />
                  }
                  label="Smart Follow-up Reminders"
                />
              </Paper>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <Paper sx={{ p: 2, height: '100%' }}>
                <Typography variant="subtitle1" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                  <TagIcon sx={{ mr: 1 }} /> Smart Organization
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.automation?.autoTagging || false}
                      onChange={(e) => handleSettingChange('automation.autoTagging', e.target.checked)}
                    />
                  }
                  label="Auto-tagging"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.automation?.duplicateDetection || false}
                      onChange={(e) => handleSettingChange('automation.duplicateDetection', e.target.checked)}
                    />
                  }
                  label="Duplicate Detection"
                />
              </Paper>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <Paper sx={{ p: 2, height: '100%' }}>
                <Typography variant="subtitle1" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                  <AnalyticsIcon sx={{ mr: 1 }} /> Lead Intelligence
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.automation?.scoreLeads || false}
                      onChange={(e) => handleSettingChange('automation.scoreLeads', e.target.checked)}
                    />
                  }
                  label="Lead Scoring"
                />
              </Paper>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Team & Collaboration */}
      <Accordion sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <TeamIcon sx={{ mr: 2, color: 'primary.main' }} />
            <Typography variant="h6">Team & Collaboration</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.team?.sharing || false}
                    onChange={(e) => handleSettingChange('team.sharing', e.target.checked)}
                  />
                }
                label="Enable Team Sharing"
              />
            </Grid>
            
            {settings.team?.sharing && (
              <>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Default Permission Level</InputLabel>
                    <Select
                      value={settings.team?.permissions || 'view'}
                      onChange={(e) => handleSettingChange('team.permissions', e.target.value)}
                    >
                      <MenuItem value="view">View Only</MenuItem>
                      <MenuItem value="edit">View & Edit</MenuItem>
                      <MenuItem value="admin">Full Admin</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    Team members will be able to access shared contacts with the selected permission level.
                  </Typography>
                </Grid>
              </>
            )}
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* API & Developer Settings */}
      <Accordion sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <ApiKeyIcon sx={{ mr: 2, color: 'primary.main' }} />
            <Typography variant="h6">API & Developer Settings</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.api?.enabled || false}
                    onChange={(e) => handleSettingChange('api.enabled', e.target.checked)}
                  />
                }
                label="Enable API Access"
              />
            </Grid>
            
            {settings.api?.enabled && (
              <>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="API Key"
                    value={settings.api?.apiKey || ''}
                    onChange={(e) => handleSettingChange('api.apiKey', e.target.value)}
                    type="password"
                    helperText="Your API key for accessing MySocialBiz API"
                    InputProps={{
                      endAdornment: (
                        <Button size="small" variant="outlined">
                          Generate New
                        </Button>
                      ),
                    }}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <Paper sx={{ p: 2, bgcolor: 'action.hover' }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                      <WebhookIcon sx={{ mr: 1 }} /> Webhooks
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Configure webhooks to receive real-time notifications when contacts are added or updated.
                    </Typography>
                    <Button variant="outlined" size="small">
                      Manage Webhooks
                    </Button>
                  </Paper>
                </Grid>
              </>
            )}
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Notifications */}
      <Accordion sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <NotificationsIcon sx={{ mr: 2, color: 'primary.main' }} />
            <Typography variant="h6">Notifications</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" sx={{ mb: 2 }}>Communication Preferences</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.notifications?.email || false}
                      onChange={(e) => handleSettingChange('notifications.email', e.target.checked)}
                    />
                  }
                  label="Email Notifications"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.notifications?.push || false}
                      onChange={(e) => handleSettingChange('notifications.push', e.target.checked)}
                    />
                  }
                  label="Push Notifications"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.notifications?.followUpReminders || false}
                      onChange={(e) => handleSettingChange('notifications.followUpReminders', e.target.checked)}
                    />
                  }
                  label="Follow-up Reminders"
                />
              </Box>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" sx={{ mb: 2 }}>Content Preferences</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.notifications?.marketing || false}
                      onChange={(e) => handleSettingChange('notifications.marketing', e.target.checked)}
                    />
                  }
                  label="Marketing Updates"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.notifications?.weeklyReports || false}
                      onChange={(e) => handleSettingChange('notifications.weeklyReports', e.target.checked)}
                    />
                  }
                  label="Weekly Reports"
                />
              </Box>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Display Preferences */}
      <Accordion sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <DisplayIcon sx={{ mr: 2, color: 'primary.main' }} />
            <Typography variant="h6">Display Preferences</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Theme</InputLabel>
                <Select
                  value={settings.theme || 'light'}
                  onChange={(e) => handleSettingChange('theme', e.target.value)}
                >
                  <MenuItem value="light">Light</MenuItem>
                  <MenuItem value="dark">Dark</MenuItem>
                  <MenuItem value="auto">Auto</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Default View</InputLabel>
                <Select
                  value={settings.display?.defaultView || 'grid'}
                  onChange={(e) => handleSettingChange('display.defaultView', e.target.value)}
                >
                  <MenuItem value="grid">Grid</MenuItem>
                  <MenuItem value="list">List</MenuItem>
                  <MenuItem value="cards">Cards</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Cards Per Page</InputLabel>
                <Select
                  value={settings.display?.cardsPerPage || 10}
                  onChange={(e) => handleSettingChange('display.cardsPerPage', e.target.value)}
                >
                  <MenuItem value={5}>5</MenuItem>
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={20}>20</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={settings.display?.sortBy || 'updatedAt'}
                  onChange={(e) => handleSettingChange('display.sortBy', e.target.value)}
                >
                  <MenuItem value="updatedAt">Last Updated</MenuItem>
                  <MenuItem value="createdAt">Date Created</MenuItem>
                  <MenuItem value="fullName">Name</MenuItem>
                  <MenuItem value="orgName">Company</MenuItem>
                  <MenuItem value="priority">Priority</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.display?.compactMode || false}
                    onChange={(e) => handleSettingChange('display.compactMode', e.target.checked)}
                  />
                }
                label="Compact Mode"
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Privacy & Security */}
      <Accordion sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <SecurityIcon sx={{ mr: 2, color: 'primary.main' }} />
            <Typography variant="h6">Privacy & Security</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" sx={{ mb: 2 }}>Data Privacy</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.privacy?.shareAnalytics || false}
                      onChange={(e) => handleSettingChange('privacy.shareAnalytics', e.target.checked)}
                    />
                  }
                  label="Share Anonymous Analytics"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.privacy?.cookieConsent || false}
                      onChange={(e) => handleSettingChange('privacy.cookieConsent', e.target.checked)}
                    />
                  }
                  label="Cookie Consent"
                />
              </Box>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" sx={{ mb: 2 }}>Data Management</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.privacy?.backupData || false}
                      onChange={(e) => handleSettingChange('privacy.backupData', e.target.checked)}
                    />
                  }
                  label="Backup Data to Cloud"
                />
                <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                  <InputLabel>Data Retention Period</InputLabel>
                  <Select
                    value={settings.privacy?.dataRetention || '2years'}
                    onChange={(e) => handleSettingChange('privacy.dataRetention', e.target.value)}
                  >
                    <MenuItem value="1year">1 Year</MenuItem>
                    <MenuItem value="2years">2 Years</MenuItem>
                    <MenuItem value="5years">5 Years</MenuItem>
                    <MenuItem value="indefinite">Indefinite</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Grid>
            
            <Grid item xs={12}>
              <Paper sx={{ p: 2, bgcolor: 'action.hover' }}>
                <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                  <BackupIcon sx={{ mr: 1 }} /> Data Export & Backup
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Export your data or request account deletion at any time.
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button variant="outlined" size="small" startIcon={<DownloadIcon />}>
                    Export Data
                  </Button>
                  <Button variant="outlined" size="small" color="error">
                    Delete Account
                  </Button>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Help & Support */}
      <Accordion sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <HelpIcon sx={{ mr: 2, color: 'primary.main' }} />
            <Typography variant="h6">Help & Support</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <Card sx={{ textAlign: 'center', p: 2 }}>
                <InfoIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
                <Typography variant="h6" gutterBottom>
                  Documentation
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Learn how to use MySocialBiz effectively
                </Typography>
                <Button variant="outlined" size="small">
                  View Docs
                </Button>
              </Card>
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <Card sx={{ textAlign: 'center', p: 2 }}>
                <FeedbackIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
                <Typography variant="h6" gutterBottom>
                  Feedback
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Help us improve MySocialBiz
                </Typography>
                <Button variant="outlined" size="small">
                  Send Feedback
                </Button>
              </Card>
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <Card sx={{ textAlign: 'center', p: 2 }}>
                <ContactsIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
                <Typography variant="h6" gutterBottom>
                  Contact Support
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Get help from our support team
                </Typography>
                <Button variant="outlined" size="small">
                  Contact Us
                </Button>
              </Card>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Dialog Components */}
      <GroupsManager 
        open={groupsDialog} 
        onClose={() => setGroupsDialog(false)}
        onGroupSelect={() => {}}
      />
      
      <TagsManager 
        open={tagsDialog} 
        onClose={() => setTagsDialog(false)}
        onTagSelect={() => {}}
      />
    </Box>
  );
}

export default Setting;
